import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CameraAltOutlined,
  CheckCircleRounded,
  CancelRounded,
} from '@mui/icons-material';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import defaultCam from '../../assets/images/graphics/default-cam.svg';
import personOutline from '../../assets/images/graphics/person.svg';
import {
  awsUpload,
  getStorageLink,
} from '../../utils/api_service/storage_api';
import { updateProfilePic } from '../../utils/api_service/user_api';
import { getPermissions } from '../../utils/helpers/auth';
import { RootState } from '../../utils/redux_store/store';
import { IS_IOS } from '../assessment/perform/assigned_assessments/assigned_assessments';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';

export const IdentityCheck: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const videoRef = useRef<HTMLVideoElement>(null);
  const photoRef = useRef<HTMLCanvasElement>(null);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [picDisabled, setPicDisabled] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    getUserCamera();
  }, [videoRef]);

  useEffect(() => {
    const processImage = async () => {
      if (isProcessing) {
        const video = videoRef.current;
        const photo = photoRef.current;
        if (video && photo) {
          const ctx = photo.getContext('2d');
          photo.width = 400;
          photo.height = video.videoHeight;
          ctx?.drawImage(video, 0, 0, 400, 480);
          setDisabled(false);
          setVerified(true);
        }
        setIsProcessing(false);
        setPicDisabled(false);
      }
    };
    processImage();
  }, [isProcessing]);

  useEffect(() => {
    const canvas = photoRef.current;
    const video = videoRef.current;
    const image = new Image();
    if (canvas && video) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = 200;
        canvas.height = 240;
        image.src = defaultCam;
        image.onload = () => {
          ctx.drawImage(image, 0, 0, 200, 240);
        };
      }
    }
  }, []);

  const stopCamera = () => {
    const video = videoRef.current;
    if (video && video.srcObject) {
      const stream = video.srcObject as MediaStream;
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      video.srcObject = null;
    }
  };

  const getUserCamera = () => {
    navigator.mediaDevices
      .getUserMedia({
        video: true,
      })
      .then((stream) => {
        const video = videoRef.current;
        if (video) {
          video.muted = true;
          video.srcObject = stream;
          video.autoplay = true;
        }
      });
  };

  const takePicture = async () => {
    setIsProcessing(true);
    setPicDisabled(true);
  };

  const uploadPicture = async (
    fileName: string,
    folderName: string,
    image: Blob,
  ) => {
    try {
      const storageLinkResponse = await getStorageLink(fileName, folderName);
      await awsUpload(storageLinkResponse.url, image);
      const updateProfilePicResponse = await updateProfilePic(
        storageLinkResponse.fileName,
      );

      if (updateProfilePicResponse.id) {
        stopCamera();

        if (localStorage.getItem('id')) {
          localStorage.setItem('profilePic', updateProfilePicResponse.profilePic);
          forwardByRole();
        } else {
          localStorage.clear();
          history.push('/login');
        }
      } else {
        toast.error(t(AUTH_KEYS.identityCheck.errorSettingProfilePic), {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error(t(AUTH_KEYS.identityCheck.errorUploadingPicture), { position: 'bottom-center' });
    }
  };

  const capturePicture = (imageName: string) => {
    const folderName = 'profile-pic';
    const photo = photoRef.current;
    if (photo) {
      photo.toBlob(
        (blob) => {
          if (blob) {
            uploadPicture(imageName, folderName, blob);
          }
        },
        'image/jpeg',
        0.9,
      );
    }
  };

  const forwardByRole = () => {
    const token = localStorage.getItem('token');
    const perms = token ? getPermissions(token) : [];
    history.push(perms.length === 0 ? '/assigned-assessments' : '/home');
  };

  const skip = () => {
    if (localStorage.getItem('id') || localStorage.getItem('email')) {
      forwardByRole();
    } else {
      localStorage.clear();
      history.push('/login');
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout leftContent={<></>}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        {/* Header Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', borderBottom: '1px solid #f1f5f9', pb: 2 }}>
          <CompanyLogoBadge
            logoUrl={companyLogo}
            companyName={companyName}
            altText='HiAcuity Logo'
          />
          <Button
            onClick={() => skip()}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: '9999px',
              px: 2.5,
              borderColor: '#e2e8f0',
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc',
              },
            }}
          >
            {t(AUTH_KEYS.identityCheck.skip)}
          </Button>
        </Box>

        {/* Title & Instructions */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: '#0f172a',
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            {t(AUTH_KEYS.identityCheck.title)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {t(AUTH_KEYS.identityCheck.instruction)}
          </Typography>
        </Box>

        {/* Camera and Capture Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            my: 1,
          }}
        >
          {/* Video Feed */}
          <Stack spacing={1.5} alignItems="center">
            <Box
              sx={{
                position: 'relative',
                width: { xs: 200, sm: 160 },
                height: { xs: 240, sm: 200 },
                borderRadius: '16px',
                overflow: 'hidden',
                bgcolor: '#0f172a',
                border: '2px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline={IS_IOS}
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {/* Overlay person outline */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  component="img"
                  src={personOutline}
                  alt="Person Outline"
                  sx={{ width: '80%', height: 'auto', opacity: 0.7 }}
                />
              </Box>
            </Box>

            <Button
              onClick={() => takePicture()}
              disabled={picDisabled}
              variant="contained"
              startIcon={<CameraAltOutlined />}
              sx={{
                borderRadius: '9999px',
                py: 0.75,
                px: 2.5,
                bgcolor: '#050508',
                color: '#ffffff',
                fontSize: '0.8125rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: '#1e293b' },
              }}
            >
              {t(AUTH_KEYS.identityCheck.takePicture)}
            </Button>
          </Stack>

          {/* Canvas Snapshot & Verification Indicators */}
          <Stack spacing={1.5} alignItems="center">
            <Box
              sx={{
                position: 'relative',
                width: { xs: 200, sm: 160 },
                height: { xs: 240, sm: 200 },
                borderRadius: '16px',
                overflow: 'hidden',
                bgcolor: '#f8fafc',
                border: '2px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <canvas
                ref={photoRef}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />

              {isProcessing && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(15, 23, 42, 0.75)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    p: 2,
                    textAlign: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={24} sx={{ color: '#34d399' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {t(AUTH_KEYS.identityCheck.processingImage)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Indicators */}
            <Stack spacing={0.75} sx={{ width: '100%', px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>
                  {t(AUTH_KEYS.identityCheck.fullFace)}
                </Typography>
                {verified ? (
                  <CheckCircleRounded sx={{ fontSize: 18, color: '#10b981' }} />
                ) : (
                  <CancelRounded sx={{ fontSize: 18, color: '#ef4444' }} />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>
                  {t(AUTH_KEYS.identityCheck.lighting)}
                </Typography>
                {verified ? (
                  <CheckCircleRounded sx={{ fontSize: 18, color: '#10b981' }} />
                ) : (
                  <CancelRounded sx={{ fontSize: 18, color: '#ef4444' }} />
                )}
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* Final Action Button */}
        <Box sx={{ pt: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button
            fullWidth
            onClick={() => capturePicture('test.jpg')}
            disabled={disabled}
            variant="contained"
            sx={{
              py: 1.5,
              px: 3,
              borderRadius: '9999px',
              bgcolor: '#050508',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.16)',
              '&:hover': {
                bgcolor: '#1e293b',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.22)',
              },
            }}
          >
            {t(AUTH_KEYS.identityCheck.completeRegistration)}
          </Button>
        </Box>
      </Box>
    </AuthenticationUiLayout>
  );
};
